/**
 * SharedView — public Gantt view (no auth required) accessible via /share/{token}
 * and /share/{token}/iframe.
 *
 * VISUAL CONTRACT: this view must look IDENTICAL to the internal Gantt at
 * `/projects/{id}?tab=gantt`. The only differences are:
 *   - No back-to-projects button (no auth, no sidebar nav)
 *   - No tab strip (only the Gantt tab makes sense for public consumers)
 *   - The version selector lives in a single dropdown that combines all
 *     allowed (project × sync) combos exposed by the link, and the user
 *     cannot pick anything outside that scope.
 *   - No AI button (would require an authenticated session).
 * Everything else — fonts, colours, paddings, the Gantt itself — is the
 * same component instance with the same defaults.
 *
 * Iframe mode notes (for Power BI embedding):
 *  - Does NOT touch localStorage (sandboxed sites can throw on storage access)
 *  - All state derives from URL + server response
 *  - Sends initial size + nav events via postMessage to parent
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import GanttView from "../components/GanttView";
import { useI18n } from "../context/I18nContext";
import { logger } from "../lib/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Dedicated axios instance for public share endpoints — no cookies, no XSRF
// token lookup. Power BI wraps iframes in `sandbox` without `allow-same-origin`,
// which would otherwise throw "Failed to read the 'cookie' property" when
// axios tries to read the XSRF cookie.
const publicHttp = axios.create({
  withCredentials: false,
  withXSRFToken: false,
  xsrfCookieName: null,
});

export default function SharedView() {
  const { token } = useParams();
  const location = useLocation();
  const isIframe = location.pathname.endsWith("/iframe");
  const { t, lang, setLang } = useI18n();

  const [data, setData] = useState(null);
  const [tasksBySync, setTasksBySync] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [errorStatus, setErrorStatus] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        // Meta only. Tasks are loaded per version below — a single payload
        // with every version of a multi-project link hits the server budget
        // and silently starves the last versions (iter47 production bug).
        const url = isIframe
          ? `${API}/share/${token}/iframe-data?include_tasks=false`
          : `${API}/share/${token}`;
        const r = await publicHttp.get(url);
        setData(isIframe ? r.data.meta : r.data);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
        setErrorStatus(e.response?.status || 0);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, isIframe]);

  // PowerBI iframe communication: notify parent on size changes
  useEffect(() => {
    if (!isIframe) return;
    const post = () => {
      try {
        window.parent?.postMessage(
          { type: "cornerstone-share", token, height: document.body.scrollHeight },
          "*",
        );
      } catch (e) {
        // iframe sandboxed — postMessage to parent is best-effort
        logger.debug("share view postMessage failed", e);
      }
    };
    post();
    const ro = new ResizeObserver(post);
    ro.observe(document.body);
    return () => ro.disconnect();
  }, [isIframe, token]);

  // Build flat list of allowed (project_id, sync_id) combos exposed by
  // this link. Order matches the link's selection so the dropdown shows
  // the user-curated order.
  const allowedCombos = useMemo(() => {
    if (!data) return [];
    const out = [];
    for (const p of data.selection || []) {
      const proj = (data.projects || {})[p.project_id];
      for (const sid of p.sync_ids || []) {
        const s = (data.syncs || {})[sid];
        if (!s) continue;
        out.push({
          project_id: p.project_id,
          sync_id: sid,
          project_name: proj?.name || "—",
          project_color: proj?.color || "#F97316",
          sync_name: s.name,
          sync_version: s.version,
          is_baseline: !!s.is_baseline,
          task_count: s.task_count,
        });
      }
    }
    return out;
  }, [data]);

  // Active combo. Single-combo links auto-select; multi-combo defaults to
  // the FIRST option (matches MS Project's "open last view" UX). The
  // chooser is a plain <select> styled exactly like the internal Gantt's
  // version dropdown.
  const [activeKey, setActiveKey] = useState(null);
  useEffect(() => {
    if (allowedCombos.length && activeKey === null) {
      setActiveKey(`${allowedCombos[0].project_id}::${allowedCombos[0].sync_id}`);
    }
  }, [allowedCombos, activeKey]);

  const activeCombo = useMemo(() => {
    if (!activeKey) return null;
    return allowedCombos.find((c) => `${c.project_id}::${c.sync_id}` === activeKey) || null;
  }, [activeKey, allowedCombos]);

  const selection = useMemo(() => (
    activeCombo
      ? [{ project_id: activeCombo.project_id, sync_id: activeCombo.sync_id }]
      : []
  ), [activeCombo]);

  // Filter the already-loaded `tasks` to the active sync. Each version is
  // fetched on demand (and cached) so no link is ever too big to render.
  const activeSyncId = activeCombo?.sync_id || null;
  const visibleTasks = useMemo(
    () => (activeSyncId ? tasksBySync[activeSyncId] || [] : []),
    [tasksBySync, activeSyncId],
  );
  const tasksReady = !activeSyncId || Array.isArray(tasksBySync[activeSyncId]);

  useEffect(() => {
    if (!activeSyncId || tasksBySync[activeSyncId]) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await publicHttp.get(`${API}/share/${token}/tasks`, { params: { sync_ids: activeSyncId } });
        if (!cancelled) setTasksBySync((prev) => ({ ...prev, [activeSyncId]: r.data.tasks || [] }));
      } catch (e) {
        logger.debug("share tasks fetch failed", e);
        if (!cancelled) setTasksBySync((prev) => ({ ...prev, [activeSyncId]: [] }));
      }
    })();
    return () => { cancelled = true; };
  }, [activeSyncId, token, tasksBySync]);

  const syncMeta = useMemo(() => {
    const m = {};
    Object.entries(data?.syncs || {}).forEach(([id, s]) => {
      const proj = (data?.projects || {})[s.project_id];
      m[id] = { name: s.name, version: s.version, color: proj?.color };
    });
    return m;
  }, [data]);

  // The available-columns catalog must reflect ONLY the active sync —
  // showing custom fields from a sync the user isn't currently viewing
  // would surface column names with no data behind them.
  const availableColumnsCatalog = useMemo(() => {
    if (!activeCombo) return [];
    const s = (data?.syncs || {})[activeCombo.sync_id];
    return s?.available_columns || [];
  }, [data, activeCombo]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-white font-mono text-xs uppercase tracking-widest text-zinc-500">{t("loading")}</div>;
  }
  if (error) {
    const isExpired = errorStatus === 410;
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className={`bg-white border rounded-md p-8 text-center max-w-md ${isExpired ? "border-amber-300" : "border-red-200"}`} data-testid={isExpired ? "share-expired" : "share-error"}>
          <div className={`cs-label ${isExpired ? "text-amber-700" : "text-red-700"}`}>{isExpired ? t("share_link_expired") : t("error")}</div>
          <p className="text-sm text-zinc-700 mt-2">
            {isExpired ? t("share_link_expired_help") : error}
          </p>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // RENDER — mirrors ProjectDetail.jsx structure exactly so the visual
  // diff between internal & shared Gantts is reduced to: (a) absence of
  // sidebar nav (handled by router layout, not us); (b) different page
  // header content; (c) constrained version selector. Container
  // paddings & background match internal.
  // ------------------------------------------------------------------
  return (
    <div className="flex flex-col min-h-screen" data-testid="shared-view">
      {/* Top header — matches ProjectDetail's `border-b bg-white sticky` */}
      {!isIframe && (
        <div className="border-b border-zinc-200 bg-white sticky top-0 z-10">
          <div className="px-8 lg:px-12 pt-6 pb-3 max-w-7xl">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="font-heading text-base font-bold tracking-tighter text-zinc-950">Cornerstone</span>
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-cs-accent" />
                <span className="cs-label">{t("shared_view_label")} · {data?.name}</span>
              </div>
              <select
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                className="text-xs bg-transparent border-0 cursor-pointer text-zinc-600 focus:outline-none"
                data-testid="shared-language-select"
                aria-label="Language"
              >
                <option value="pt">PT</option>
                <option value="en">EN</option>
              </select>
            </div>
            <div className="flex items-end justify-between mt-2">
              <div>
                <div className="flex items-center gap-2">
                  {activeCombo && (
                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: activeCombo.project_color }} />
                  )}
                  <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950" data-testid="shared-project-name">
                    {activeCombo?.project_name || data?.name}
                  </h1>
                  {/* iter41: when the link is "track_latest", show a small
                      Live badge so external viewers know the page reflects
                      whatever the team syncs going forward — no need to
                      refresh the share URL. */}
                  {activeCombo && (data?.selection || []).some(
                    (p) => p.project_id === activeCombo.project_id && p.include_all_versions,
                  ) && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cs-blueprint/10 text-cs-blueprint text-[10px] font-semibold uppercase tracking-wider"
                      data-testid="shared-all-versions-badge"
                    >
                      {t("share_all_versions_badge")}
                    </span>
                  )}
                  {activeCombo && (data?.selection || []).some(
                    (p) => p.project_id === activeCombo.project_id && p.track_latest,
                  ) && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cs-accent/10 text-cs-accent text-[10px] font-semibold uppercase tracking-wider"
                      title={t("share_live_badge_tooltip")}
                      data-testid="shared-live-badge"
                    >
                      ⚡ {t("share_live_badge")}
                    </span>
                  )}
                </div>
                {data?.description && (
                  <div className="text-xs text-zinc-500 mt-1 font-mono">{data.description}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version selector strip — same styling as internal Gantt version selector */}
      {!isIframe && allowedCombos.length > 0 && (
        <div className="bg-zinc-50 border-b border-zinc-200 px-8 lg:px-12 py-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="cs-label">{t("version")}:</div>
            <select
              value={activeKey || ""}
              onChange={(e) => setActiveKey(e.target.value)}
              className="px-3 py-1.5 border border-zinc-300 rounded-md text-xs font-mono"
              data-testid="public-picker-select"
            >
              {allowedCombos.map((c) => {
                const key = `${c.project_id}::${c.sync_id}`;
                const label = allowedCombos.some((x) => x.project_id !== c.project_id)
                  // Multi-project link: prefix with project name to disambiguate
                  ? `${c.project_name} · v${c.sync_version} · ${c.sync_name}${c.is_baseline ? " (baseline)" : ""}`
                  : `v${c.sync_version} · ${c.sync_name}${c.is_baseline ? " (baseline)" : ""}`;
                return <option key={key} value={key}>{label}</option>;
              })}
            </select>
            {activeCombo?.task_count != null && (
              <span className="font-mono text-xs text-zinc-500">{activeCombo.task_count} {t("tasks")}</span>
            )}
          </div>
        </div>
      )}

      {/* Gantt container — IDENTICAL classes to ProjectDetail gantt tab */}
      <div className={isIframe ? "p-3 flex-1" : "px-4 lg:px-6 py-4 w-full flex-1"}>
        {!tasksReady ? (
          <div
            className="border border-zinc-200 rounded-md bg-white py-20 text-center font-mono text-xs uppercase tracking-widest text-zinc-500"
            data-testid="public-tasks-loading"
          >
            {t("loading")}
          </div>
        ) : (
          <GanttView
            key={activeKey || "no-combo"}
            initialSelection={selection}
            initialTasks={visibleTasks}
            shareToken={token}
            syncMeta={syncMeta}
            projects={data?.projects || {}}
            availableColumnsCatalog={availableColumnsCatalog}
            projectName={activeCombo?.project_name || data?.name || "share"}
            embeddable={isIframe}
            isPublic
          />
        )}
      </div>
    </div>
  );
}
